import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

interface UpdateAutoLinkingStudiesModalProps {
  removedStudyNames: Array<string>;
  addedStudyNames: Array<string>;
  onRequestSubmit: (result: {}) => void;
  onRequestClose: () => void;
}

export default function UpdateAutoLinkingStudiesModal({
  removedStudyNames,
  addedStudyNames,
  onRequestSubmit,
  onRequestClose,
}: UpdateAutoLinkingStudiesModalProps): JSX.Element {
  const removedStudyNamesText = removedStudyNames.join(', ');
  const addedStudyNamesText = addedStudyNames.join(', ');
  const isRemovingOnly = addedStudyNames.length === 0;

  if (!BenchlingConfig.STUDIES_ENTRY_AUTO_LINKING_EXTENSIONS) {
    return (
      <ConfirmationModal
        title="Unlink entry from study"
        confirmButtonStyle={ButtonStyle.NEGATIVE}
        onRequestSubmit={onRequestSubmit}
        onRequestClose={onRequestClose}
      >
        <p className={css(styles.modalBody)}>
          Any new items created in this entry will no longer be automatically linked to{' '}
          <strong>{removedStudyNamesText}</strong>.
        </p>
        <p className={css(styles.modalBody)}>
          Any previously created items will maintain their existing links to their studies. To modify or
          unlink them, you must edit each item's metadata.
        </p>
        <p className={css(styles.modalBody)}>
          This action is irreversible. You will not be able to re-establish automatic linking for this study
          after removal.
        </p>
      </ConfirmationModal>
    );
  }

  let title: string;
  let changeMessage: JSX.Element;
  if (isRemovingOnly) {
    title = 'Remove auto-linking study';
    changeMessage = getRemoveAutoLinkingMessage(removedStudyNamesText);
  } else if (removedStudyNames.length === 0 && addedStudyNames.length > 0) {
    title = 'Add auto-linking study';
    changeMessage = getAddAutoLinkingMessage(addedStudyNamesText);
  } else {
    title = 'Change auto-linking study';
    changeMessage = getChangeAutoLinkingMessage(addedStudyNamesText, removedStudyNamesText);
  }

  return (
    <ConfirmationModal
      title={title}
      confirmButtonStyle={isRemovingOnly ? ButtonStyle.NEGATIVE : ButtonStyle.PRIMARY}
      onRequestSubmit={onRequestSubmit}
      onRequestClose={onRequestClose}
    >
      <p className={css(styles.modalBody)}>{changeMessage}</p>
      <p className={css(styles.modalBody)}>
        Previously created entities keep their existing study links. To change or remove a link, edit that
        entity's metadata.
      </p>
    </ConfirmationModal>
  );
}

function ConfirmationModal({
  title,
  confirmButtonStyle,
  onRequestSubmit,
  onRequestClose,
  children,
}: {
  title: string;
  confirmButtonStyle: ButtonStyle;
  onRequestSubmit: (result: {}) => void;
  onRequestClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Modal
      title={title}
      buttons={[
        {
          text: 'Cancel',
          submit: false,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: () => {
            onRequestClose();
          },
        },
        {
          text: 'Confirm',
          submit: true,
          style: confirmButtonStyle,
        },
      ]}
      onRequestSubmit={() => {
        onRequestSubmit({});
      }}
      onRequestClose={onRequestClose}
    >
      {children}
    </Modal>
  );
}

function getRemoveAutoLinkingMessage(removedStudyNamesText: string): JSX.Element {
  return (
    <>
      New entities in this entry will no longer be automatically linked to{' '}
      <strong>{removedStudyNamesText}</strong>.
    </>
  );
}

function getAddAutoLinkingMessage(addedStudyNamesText: string): JSX.Element {
  return (
    <>
      New entities in this entry will link to <strong>{addedStudyNamesText}</strong>.
    </>
  );
}

function getChangeAutoLinkingMessage(
  addedStudyNamesText: string,
  removedStudyNamesText: string
): JSX.Element {
  return (
    <>
      New entities in this entry will link to <strong>{addedStudyNamesText}</strong> instead of{' '}
      <strong>{removedStudyNamesText}</strong>.
    </>
  );
}

const styles = StyleSheet.create({
  modalBody: {
    fontSize: '14px',
  },
});
